<template>
  <ContentWrap>
    <el-button v-hasPermi="[updatePermission]" type="primary" @click="open()">
      <Icon icon="ep:plus" class="mr-5px" />新增分类
    </el-button>
  </ContentWrap>

  <ContentWrap>
    <div class="mb-12px">
      <el-button
        v-hasPermi="[updatePermission]"
        type="success"
        plain
        :disabled="!selectedRows.length"
        @click="batchStatus(1)"
      >
        <Icon icon="ep:circle-check" class="mr-5px" />批量启用
      </el-button>
      <el-button
        v-hasPermi="[updatePermission]"
        type="warning"
        plain
        :disabled="!selectedRows.length"
        @click="batchStatus(0)"
      >
        <Icon icon="ep:circle-close" class="mr-5px" />批量禁用
      </el-button>
      <el-button
        v-hasPermi="[deletePermission]"
        type="danger"
        plain
        :disabled="!selectedRows.length"
        @click="deleteSelected"
      >
        <Icon icon="ep:delete" class="mr-5px" />批量删除
      </el-button>
      <span v-if="selectedRows.length" class="ml-10px text-12px text-#909399">
        已选择 {{ selectedRows.length }} 项
      </span>
    </div>
    <el-table
      v-loading="loading"
      :data="list"
      row-key="id"
      @selection-change="selectedRows = $event"
    >
      <el-table-column type="selection" width="45" />
      <el-table-column prop="categoryName" label="分类名称" min-width="180" />
      <el-table-column prop="sortOrder" label="排序" width="90" />
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'info'">
            {{ row.status === 1 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="220" fixed="right">
        <template #default="{ row }">
          <el-button v-hasPermi="[updatePermission]" link type="primary" @click="open(row)"
            >编辑</el-button
          >
          <el-button
            v-hasPermi="[updatePermission]"
            link
            :type="row.status === 1 ? 'warning' : 'success'"
            @click="setStatus(row)"
          >
            {{ row.status === 1 ? '禁用' : '启用' }}
          </el-button>
          <el-button v-hasPermi="[deletePermission]" link type="danger" @click="remove(row.id)"
            >删除</el-button
          >
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>

  <Dialog v-model="visible" :title="form.id ? '编辑分类' : '新增分类'">
    <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
      <el-form-item label="分类名称" prop="categoryName"
        ><el-input v-model="form.categoryName"
      /></el-form-item>
      <el-form-item label="排序"
        ><el-input-number v-model="form.sortOrder" :min="0"
      /></el-form-item>
      <el-form-item label="状态"
        ><el-switch v-model="form.status" :active-value="1" :inactive-value="0"
      /></el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" @click="save">保存</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import * as Api from '@/api/business/newsCategory'

defineOptions({ name: 'BossCircleNewsCategory' })

const message = useMessage()
const route = useRoute()
const userMode = computed(() => route.path.includes('/circle/category'))
const updatePermission = computed(() =>
  userMode.value ? 'bosscircle:user-news-category:update' : 'bosscircle:news-category:update'
)
const deletePermission = computed(() =>
  userMode.value ? 'bosscircle:user-news-category:delete' : 'bosscircle:news-category:delete'
)
const loading = ref(false)
const list = ref<any[]>([])
const selectedRows = ref<any[]>([])
const visible = ref(false)
const formRef = ref()
const form = reactive<any>({})
const rules = {
  categoryName: [{ required: true, message: '请输入分类名称' }]
}
const load = async () => {
  loading.value = true
  try {
    list.value = await Api.getCategoryList(userMode.value ? 2 : 1, userMode.value)
  } finally {
    loading.value = false
  }
}
const open = (row: any = {}) => {
  Object.assign(
    form,
    { id: undefined, categoryName: '', sortOrder: 0, status: 1, publisherType: 1 },
    row
  )
  form.publisherType = 1
  visible.value = true
}
const save = async () => {
  if (!(await formRef.value?.validate().catch(() => false))) return
  await Api.saveCategory(
    {
      id: form.id,
      categoryName: form.categoryName,
      sortOrder: form.sortOrder,
      status: form.status,
      publisherType: userMode.value ? 2 : 1
    },
    userMode.value
  )
  visible.value = false
  message.success('保存成功')
  await load()
}
const setStatus = async (row: any) => {
  const status = row.status === 1 ? 0 : 1
  await message.confirm(`确认${status === 1 ? '启用' : '禁用'}该分类吗？`)
  await Api.updateCategoryStatus(row.id, status, userMode.value)
  message.success('操作成功')
  await load()
}
const batchStatus = async (status: number) => {
  await message.confirm(
    `确认${status === 1 ? '启用' : '禁用'}选中的 ${selectedRows.value.length} 个分类吗？`
  )
  await Api.batchUpdateCategoryStatus(
    selectedRows.value.map((item) => item.id),
    status,
    userMode.value
  )
  selectedRows.value = []
  message.success('批量操作成功')
  await load()
}
const remove = async (id: number) => {
  await message.delConfirm('确认删除该分类吗？存在文章的分类不允许删除。')
  await Api.deleteCategory(id, userMode.value)
  message.success('删除成功')
  await load()
}
const deleteSelected = async () => {
  await message.delConfirm(
    `确认删除选中的 ${selectedRows.value.length} 个分类吗？存在文章的分类不允许删除。`
  )
  await Api.batchDeleteCategory(
    selectedRows.value.map((item) => item.id),
    userMode.value
  )
  selectedRows.value = []
  message.success('批量删除成功')
  await load()
}

onMounted(load)
</script>
